import type { ReactNode } from 'react';

import { Button } from '../../components/Button';
import { Checkbox } from '../../components/Checkbox';
import { Skeleton, SkeletonPulse } from '../../components/Skeleton';
import { Pressable } from '../../primitives/Pressable';
import { Text } from '../../primitives/Text';
import { View } from '../../primitives/View';
import { brandColor } from '../../styles/brand-colors';
import { useTheme } from '../../theme';
import { cn } from '../../utils/cn';
import type {
  InviteMateRow,
  InviteMatesDialogBodyProps,
} from './InviteMatesDialogBody.types';

/** How many skeleton rows stand in while the mates list resolves. */
const SKELETON_ROWS = 3;

/**
 * The `tile` header's footprint (Figma 61:3405) and the scale that gets the DS
 * card down to it — see {@link TileHeaderFrame}.
 */
const TILE_HEADER_BOX = 108;
const TILE_CARD_BOX = 180;
const TILE_HEADER_SCALE = TILE_HEADER_BOX / TILE_CARD_BOX;

/**
 * "Invite your Mates!" — the pick-your-mutuals list that every study-group
 * invite surface shows (Figma 17-21535 for the group page, 61-425 for the one
 * that follows creating a group).
 *
 * Web renders it inside a DS `Dialog`, mobile inside a DS `BottomSheet`, and
 * both hand it the same four things: a header (mascot or the real group tile),
 * an avatar renderer, the selection they own, and the two footer actions. The
 * close is the shell's: web's dialog pins one in its corner; a native sheet has
 * none (it closes by drag, backdrop or back).
 *
 * What it deliberately does NOT do:
 *   - **no selection state.** The caller owns `selectedIds`, so the
 *     pre-select-all default, the `deselectedAllFirst` analytics signal and the
 *     invite payload all live in one place per surface.
 *   - **no empty state.** A dialog offering nobody is not a worse dialog, it is
 *     the wrong surface — the caller falls back to copying the link instead.
 *
 * Labels arrive pre-translated (the DS carries no i18n).
 */
export function InviteMatesDialogBody({
  header,
  headerVariant = 'mascot',
  title,
  subtitle,
  state = 'list',
  rows,
  selectedIds,
  onRowPress,
  countLabel,
  toggleAllLabel,
  onToggleAll,
  renderAvatar,
  renderScrollArea,
  copyLinkLabel,
  onCopyLink,
  inviteLabel,
  onInvite,
  inviteDisabled,
  inviteLoading,
  icons,
  rowAccessibilityLabel,
  className,
  testID,
}: InviteMatesDialogBodyProps) {
  const { isDark } = useTheme();
  const isLoading = state === 'loading';
  const MatesIcon = icons.mates;

  const list = isLoading ? (
    <SkeletonPulse className="gap-1">
      {Array.from({ length: SKELETON_ROWS }, (_, i) => (
        <SkeletonRow key={i} />
      ))}
    </SkeletonPulse>
  ) : (
    <View className="gap-1">
      {rows.map((row) => (
        <MateRow
          key={row.userId}
          row={row}
          selected={selectedIds.has(row.userId)}
          onPress={() => onRowPress(row.userId)}
          renderAvatar={renderAvatar}
          accessibilityLabel={rowAccessibilityLabel}
          testID={testID ? `${testID}-row-${row.userId}` : undefined}
        />
      ))}
    </View>
  );

  return (
    <View className={cn('w-full flex-1 gap-5', className)} testID={testID}>
      <View className="min-h-0 w-full flex-1 items-center gap-4">
        <View className="w-full items-center">
          {headerVariant === 'tile' ? (
            <TileHeaderFrame>{header}</TileHeaderFrame>
          ) : (
            header
          )}
        </View>

        <View className="w-full items-center">
          <Text className="w-full text-center text-2xl font-bold leading-8 text-title-950">
            {title}
          </Text>
          {/* The tile header is taller than the mascot, so its subtitle drops
              to B3 to keep the list above the fold (Figma 61-425). */}
          <Text
            className={cn(
              'w-full text-center font-medium text-secondary-600',
              headerVariant === 'tile' ? 'text-xs leading-5' : 'text-base leading-6',
            )}>
            {subtitle}
          </Text>
        </View>

        {/* The list sits straight on the shell's surface. Figma 17:21565's
            inner glass container is gone: inside a sheet's content surface it
            was a card in a card (design review 2026-09: "it still has the X
            and the double container"). */}
        <View className="min-h-0 w-full flex-1 gap-4">
          {/* `pr-2` — not `pr-4`: the toggle sits over the rows' checkbox
              column, which buys the count label the breathing room a wide
              label ("Deselect All") otherwise eats on a phone. */}
          <View className="w-full flex-row items-center justify-between pl-4 pr-2 pt-[10px]">
            <View className="flex-row items-center gap-2">
              {/* Raw colour, not a class: a lucide glyph on native only takes
                  `color` unless the app registered it with `cssInterop`. */}
              <MatesIcon size={20} color={brandColor('secondary', '600', isDark)} />
              <Text className="text-sm font-bold text-body-600">{countLabel}</Text>
            </View>
            <Button
              variant="secondary"
              size="xs"
              disabled={isLoading || rows.length === 0}
              onPress={onToggleAll}
              testID={testID ? `${testID}-toggle-all` : undefined}>
              {toggleAllLabel}
            </Button>
          </View>

          <View className="min-h-0 w-full flex-1">
            {renderScrollArea ? (
              renderScrollArea(list)
            ) : (
              <View className="min-h-0 w-full flex-1 web:overflow-y-auto">{list}</View>
            )}
          </View>
        </View>
      </View>

      {/* Figma 17:21617 — Copy Link + Invite, centred, gap 8. That pair only
          fits when both labels are short, and the invite label is COUNTED, so
          the long plural in German/Polish clipped on a phone. From `sm` up
          (dialog-width surfaces, tablets) the footer is the Figma row; below
          it the two stack full-width, where no locale can truncate either
          action. `flex-col-reverse` is what buys both: source order stays
          Copy → Invite so the wide row reads left-to-right, while the stack
          puts the primary nearest the thumb. `flex-wrap` is the safety net —
          an extreme locale wraps instead of overflowing the dialog. */}
      <View className="w-full flex-col-reverse items-center gap-2 sm:flex-row sm:flex-wrap sm:justify-center">
        <Button
          variant="secondary"
          size="l"
          fullWidth
          className="sm:w-auto"
          leftIcon={icons.copyLink}
          onPress={onCopyLink}
          testID={testID ? `${testID}-copy-link` : undefined}>
          {copyLinkLabel}
        </Button>
        <Button
          variant="primary"
          size="l"
          fullWidth
          className="sm:w-auto"
          leftIcon={icons.invite}
          loading={inviteLoading}
          disabled={inviteDisabled || inviteLoading}
          onPress={() => onInvite([...selectedIds])}
          testID={testID ? `${testID}-invite` : undefined}>
          {inviteLabel}
        </Button>
      </View>
    </View>
  );
}
InviteMatesDialogBody.displayName = 'InviteMatesDialogBody';

/**
 * The group's own tile as this dialog's artwork, at the size the design draws
 * it: a 108px preview sitting where the mascot variant puts its 64px tile
 * (Figma 61:3405), NOT a full dashboard tile — a group card at its real 180px
 * dwarfed the list it is introducing.
 *
 * The frame SCALES the real card rather than asking for a smaller one: the DS
 * grid card has no size under `compact` (180×180), and a freely-resized Figma
 * instance is not a new DS variant. `absolute` inside the 108 box is what makes
 * that cross-platform — a centre-origin scale is all React Native offers, and
 * Yoga positions an inset-less absolute child by its parent's alignment, so the
 * shrunken art lands centred in the box on web and native alike.
 */
function TileHeaderFrame({ children }: { children: ReactNode }) {
  return (
    <View
      className="relative items-center justify-center"
      webProps={{ style: { width: TILE_HEADER_BOX, height: TILE_HEADER_BOX } }}
      nativeProps={{ style: { width: TILE_HEADER_BOX, height: TILE_HEADER_BOX } }}>
      <View
        className="absolute"
        webProps={{
          style: {
            width: TILE_CARD_BOX,
            height: TILE_CARD_BOX,
            transform: `scale(${TILE_HEADER_SCALE})`,
          },
        }}
        nativeProps={{
          style: {
            width: TILE_CARD_BOX,
            height: TILE_CARD_BOX,
            transform: [{ scale: TILE_HEADER_SCALE }],
          },
        }}>
        {children}
      </View>
    </View>
  );
}

/** One selectable mate. The ROW owns the checkbox semantics, not the box. */
function MateRow({
  row,
  selected,
  onPress,
  renderAvatar,
  accessibilityLabel,
  testID,
}: {
  row: InviteMateRow;
  selected: boolean;
  onPress: () => void;
  renderAvatar: (row: InviteMateRow) => ReactNode;
  accessibilityLabel?: (row: InviteMateRow, selected: boolean) => string;
  testID?: string;
}) {
  return (
    <Pressable
      accessibilityRole="checkbox"
      accessibilityLabel={accessibilityLabel?.(row, selected)}
      onPress={onPress}
      testID={testID}
      nativeProps={{ accessibilityState: { checked: selected } }}
      webProps={{ 'aria-checked': selected }}
      className={cn(
        'flex w-full flex-row items-center justify-between rounded-[20px] py-2 pl-4 pr-2',
        // Dark: the row sits on the dialog's overlay glass (`secondary-100`),
        // so its hover is the step above it (elevation.ts) — `secondary-100`
        // there vanished into the panel.
        'web:transition-colors web:hover:bg-secondary-100 dark:web:hover:bg-secondary-200',
      )}>
      <View className="min-w-0 flex-1 flex-row items-center gap-2">
        {renderAvatar(row)}
        <View className="min-w-0 flex-1">
          <Text numberOfLines={1} className="text-base font-bold text-body-600">
            @{row.handle}
          </Text>
          {row.secondaryLabel ? (
            <Text numberOfLines={1} className="text-xs font-medium text-secondary-400">
              {row.secondaryLabel}
            </Text>
          ) : null}
        </View>
      </View>
      <Checkbox interactive={false} checked={selected} variant="primary" size="m" />
    </Pressable>
  );
}

/** Placeholder row — same geometry as {@link MateRow} so nothing jumps. */
function SkeletonRow() {
  return (
    <View className="w-full flex-row items-center justify-between rounded-[20px] py-2 pl-4 pr-2">
      <View className="flex-1 flex-row items-center gap-2">
        <Skeleton shape="circle" className="h-9 w-9" />
        <View className="gap-1">
          <Skeleton shape="line" className="w-28" />
          <Skeleton shape="line" className="h-3 w-20" />
        </View>
      </View>
      <Skeleton className="h-6 w-6 rounded-lg" />
    </View>
  );
}
